import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, post } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { Alert, Button, Checkbox, ErrorSummary, Field, TextInput } from '../components/ui.tsx';
import { PasswordInput } from '../components/PasswordInput.tsx';

function AuthFrame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-navy-900 to-navy-800 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-navy-900">
        <div className="mb-6 flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="h-9 w-9" />
          <span className="text-lg font-semibold text-navy-900 dark:text-white">Broadcast System</span>
        </div>
        <h1 className="mb-4 text-xl font-semibold text-navy-900 dark:text-white">{title}</h1>
        {children}
      </div>
    </main>
  );
}

export function LoginPage() {
  const { refresh } = useSession();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/api/auth/login', { email, password, remember });
      await refresh();
      nav('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthFrame title="Sign in">
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && <ErrorSummary title="Sign-in failed" errors={[{ path: 'form', message: error }]} />}
        <Field label="Email">
          {(f) => (
            <TextInput
              id={f.id}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              dir="ltr"
            />
          )}
        </Field>
        <Field label="Password">
          {(f) => (
            <PasswordInput
              id={f.id}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Checkbox
          label="Keep me signed in on this device"
          hint="For 30 days. Don’t use on shared computers."
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
        />
        <Button type="submit" variant="primary" busy={busy} className="w-full">
          Sign in
        </Button>
        <p className="text-center text-sm">
          <Link to="/forgot-password" className="text-teal-700 underline dark:text-teal-300">
            Forgot your password?
          </Link>
        </p>
      </form>
    </AuthFrame>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthFrame title="Reset your password">
      {done ? (
        <Alert tone="success">{done}</Alert>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              const r = await post<{ message: string }>('/api/auth/password-reset/request', { email });
              setDone(r.message);
            } catch (err) {
              setDone(err instanceof ApiError ? err.message : 'Please try again later.');
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field
            label="Email"
            hint="If email delivery is not configured, the server administrator can issue a reset link (see the recovery instructions in My Account)."
          >
            {(f) => (
              <TextInput
                id={f.id}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                dir="ltr"
              />
            )}
          </Field>
          <Button type="submit" variant="primary" busy={busy} className="w-full">
            Send reset link
          </Button>
        </form>
      )}
      <p className="mt-4 text-center text-sm">
        <Link to="/login" className="text-teal-700 underline dark:text-teal-300">
          Back to sign in
        </Link>
      </p>
    </AuthFrame>
  );
}

/** Complete a reset or the first-owner setup. The token is in the URL fragment, never sent to the server logs. */
export function SetPasswordPage({ purpose }: { purpose: 'reset' | 'setup' }) {
  const token = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [errors, setErrors] = useState<Array<{ path: string; message: string }>>([]);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    const errs = [];
    if (pw.length < 10) errs.push({ path: 'pw', message: 'Use at least 10 characters.' });
    if (pw !== pw2) errs.push({ path: 'pw2', message: 'The passwords do not match.' });
    setErrors(errs);
    if (errs.length) return;
    setBusy(true);
    try {
      await post(purpose === 'setup' ? '/api/auth/setup/complete' : '/api/auth/password-reset/complete', {
        token,
        password: pw,
      });
      history.replaceState(null, '', window.location.pathname);
      setDone(true);
    } catch (err) {
      setErrors([
        { path: 'form', message: err instanceof ApiError ? err.message : 'Could not set the password.' },
      ]);
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthFrame title={purpose === 'setup' ? 'Set up your owner account' : 'Choose a new password'}>
      {!token ? (
        <Alert tone="danger">This link is incomplete. Use the full link you were given.</Alert>
      ) : done ? (
        <div className="space-y-4">
          <Alert tone="success">Your password has been set. All other sessions were signed out.</Alert>
          <Link to="/login" className="block text-center text-teal-700 underline dark:text-teal-300">
            Sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" noValidate>
          <ErrorSummary errors={errors} />
          <Field
            label="New password"
            hint="At least 10 characters."
            error={errors.find((x) => x.path === 'pw')?.message}
          >
            {(f) => (
              <PasswordInput
                id={f.id}
                aria-describedby={f.describedBy}
                invalid={f.invalid}
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
              />
            )}
          </Field>
          <Field label="Repeat the password" error={errors.find((x) => x.path === 'pw2')?.message}>
            {(f) => (
              <PasswordInput
                id={f.id}
                aria-describedby={f.describedBy}
                invalid={f.invalid}
                autoComplete="new-password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" busy={busy} className="w-full">
            Set password
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}
